import SiniestrosCompGrid from 'components/Siniestros/SiniestrosCompGrid'
import { useUser } from 'contexts/UserContext'
import useFetch from 'hooks/useFetch'
import { useToast } from 'hooks/useToast'
import { useEffect, useCallback, useState, useRef } from 'react'
import XLSX from 'xlsx-js-style'

const SHEET_NAME = 'Siniestro'
const ARCHIVED_ESTADOS = ['LIQUIDADO', 'CANCELADO', 'CERRADO']

const toStr = (v) => (v === null || v === undefined ? '' : String(v))
const isEmptyRow = (row) =>
  Object.values(row || {}).every((v) => toStr(v).trim() === '')

const HEADER_MAP = {
  'ID':                       'IDInterno',
  'REF. ALBARAN':             'ref_albaran',
  'FECHA SINIESTRO':          'fecha_siniestro',
  'CAUSA':                    'causa',
  'TIPO PAGO':                'tipo_pago',
  'DELEGACIÓN PROPIETARIA':   'delegacion_propietaria',
  'FECHA DE DIA':             'fecha_dia',
  'REF FAST':                 'ref_fast',
  'ESTADO SINIESTRO':         'estado_siniestro',
  'COMENTARIOS':              'comentarios',
  'IMPORTE RECLAMADO':        'importe_reclamado',
  'FRANQUICIA APLICABLE':     'franquicia_aplicable',
  'IMPORTE NETO A COBRAR':    'importe_neto_cobrar',
  'NOMBRE DEL FRANQUICIADO':  'nombre_franquiciado',
  'DESCRIPCIÓN MERCANCÍA':    'descripcion_mercancia',
  'PESO MERCANCÍA (Kg)':      'peso_mercancia_kg',
  'CUENTA':                   'cuenta',
  'CONCEPTO FACTURA':         'concepto_factura',
  'MARGEN %':                 'margen_porcentaje',
  'RECLAMACION':              'reclamacion',
  'PARTE EXTRAVIO':           'parte_extravio',
  'ALBARAN':                  'albaran',
  'FACTURA DE COMPRA':        'factura_compra',
  'DENUNCIA':                 'denuncia',
  'FACTURA DE VENTA':         'factura_venta',
  'EXPEDIENTE COMPLETO':      'expediente_completo',
}

const REQUIRED = ['IDInterno']

export default function SiniestrosViewGrid() {
  const { showToast } = useToast()
  const { user } = useUser()

  const [fetched, fetchAll] = useFetch()
  const [fetchedArchived, fetchAllArchived] = useFetch()
  const [deleted, deleteOne] = useFetch()

  const archivedLoadedRef = useRef([])
  const archivedDataRef = useRef([]) 
  const [combinedData, setCombinedData] = useState(null)

  const handleRefresh = useCallback(async () => {
    archivedLoadedRef.current = []
    archivedDataRef.current = []       // ← NUEVO
    await fetchAll({ url: 'siniestros', method: 'GET' })
  }, [fetchAll])

  const handleLoadArchived = useCallback(async (estados) => {
    const nuevos = (Array.isArray(estados) ? estados : [estados])
      .map((e) => String(e).trim().toUpperCase())
      .filter((e) => !archivedLoadedRef.current.includes(e))

    if (!nuevos.length) return

    archivedLoadedRef.current = [...archivedLoadedRef.current, ...nuevos]

    const params = nuevos.map((e) => `estados[]=${encodeURIComponent(e)}`).join('&')
    await fetchAllArchived({ url: `siniestros?${params}`, method: 'GET' })
  }, [fetchAllArchived])

  useEffect(() => { handleRefresh() }, [])

  // Combina base + archivados sin duplicar
  useEffect(() => {
    const newArchived = fetchedArchived?.data
    if (!newArchived || !newArchived.length) return

    const existingIds = new Set(archivedDataRef.current.map((s) => s.id))
    const toAdd = newArchived.filter(
      (s) =>
        ARCHIVED_ESTADOS.includes(String(s.estado_siniestro || '').trim().toUpperCase()) &&
        !existingIds.has(s.id)
    )
    if (toAdd.length > 0) {
      archivedDataRef.current = [...archivedDataRef.current, ...toAdd]
    }

    // Recombina con la base actual
    const base = fetched?.data
    if (!base) return
    const baseIds = new Set(base.map((s) => s.id))
    const onlyArchived = archivedDataRef.current.filter((s) => !baseIds.has(s.id))
    setCombinedData([...base, ...onlyArchived])
  }, [fetchedArchived?.data])

  // Cuando cambia la base, recombina con los archivados acumulados
  useEffect(() => {
    const base = fetched?.data
    if (!base) { setCombinedData(null); return }
    if (!archivedDataRef.current.length) { setCombinedData(base); return }
    const baseIds = new Set(base.map((s) => s.id))
    const onlyArchived = archivedDataRef.current.filter((s) => !baseIds.has(s.id))
    setCombinedData([...base, ...onlyArchived])
  }, [fetched?.data])

  const handleDelete = async (id) => {
    await deleteOne({
      url: `siniestros/${user.id}/${id}`,
      method: 'DELETE',
      messageKo: '¡Error eliminando siniestro!'
    })
  }

  useEffect(() => {
    if (deleted.isCalled) {
      if (deleted.message) { showToast(deleted.message, 'success'); handleRefresh() }
      if (deleted.error) { showToast(deleted.error, 'error') }
    }
  }, [deleted.loading])

  const handleAnalyzeExcel = useCallback(async (file) => {
    if (!file) throw new Error('No se ha seleccionado ningún archivo.')
    if (!file.name?.toLowerCase().endsWith('.xlsx'))
      throw new Error('El archivo debe ser .xlsx')

    const buffer = await file.arrayBuffer()
    const workbook = XLSX.read(buffer, { type: 'array' })

    const sheetName = workbook.SheetNames?.includes(SHEET_NAME)
      ? SHEET_NAME
      : workbook.SheetNames?.[0]

    if (!sheetName) throw new Error('El archivo Excel no contiene hojas.')
    const sheet = workbook.Sheets[sheetName]
    if (!sheet) throw new Error(`No se pudo leer la hoja: ${sheetName}`)

    const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false })
    if (!rawRows?.length) throw new Error('La hoja está vacía.')

    const logs = []
    if (sheetName !== SHEET_NAME) {
      logs.push({ type: 'warning', message: `No se encontró la hoja "${SHEET_NAME}". Se usará "${sheetName}".` })
    }

    const excelHeaders = Object.keys(rawRows?.[0] || {})
    const mappedHeaders = excelHeaders.map((h) => HEADER_MAP[h] || HEADER_MAP[toStr(h)] || null).filter(Boolean)

    for (const req of REQUIRED) {
      if (!mappedHeaders.includes(req)) {
        logs.push({ type: 'error', message: `Falta la columna obligatoria en Excel: "${req}". (Revisa cabeceras)` })
      }
    }

    if (logs.some((l) => l.type === 'error')) return { logs, data: [] }

    const data = []
    let emptySkipped = 0

    rawRows.forEach((row, idx) => {
      if (isEmptyRow(row)) { emptySkipped++; return }

      const out = {
        IDInterno: null,
        ref_albaran: null,
        fecha_siniestro: null,
        causa: null,
        tipo_pago: null,
        delegacion_propietaria: null
      }

      for (const [excelKey, value] of Object.entries(row)) {
        const targetKey = HEADER_MAP[excelKey] || HEADER_MAP[toStr(excelKey)]
        if (!targetKey) continue
        out[targetKey] = toStr(value)
      }

      const codigo = toStr(out.IDInterno).trim()
      if (!codigo) {
        logs.push({ type: 'error', message: `Fila ${idx + 2}: falta ID.` })
        return
      }

      data.push(out)
      logs.push({ type: 'import', message: `Fila ${idx + 2}: OK (${codigo})` })
    })

    if (emptySkipped > 0) {
      logs.push({ type: 'warning', message: `Se han omitido ${emptySkipped} filas vacías.` })
    }
    if (!data.length) {
      logs.push({ type: 'error', message: 'No hay filas válidas para importar.' })
    }

    return { logs, data }
  }, [])

  return (
    <SiniestrosCompGrid
      user={user}
      data={combinedData}
      handleRefresh={handleRefresh}
      handleDelete={handleDelete}
      handleAnalyzeExcel={handleAnalyzeExcel}
      onRequestArchived={handleLoadArchived}
      archivedLoaded={archivedLoadedRef.current}
    />
  )
}